import { useTranslation } from 'react-i18next';
import { formatDay, formatWeekdayShort, formatDayOfMonth } from '@kidshome/i18n';
import { cn } from '@kidshome/ui';
import type { OccurrenceResponseStatus } from '@kidshome/api-contract';
import { useHouseholdContext } from '@/shared/lib/household';
import { parseLocalDate } from '@/shared/lib/dates';
import { DOT_TONE, countStatuses } from '@/widgets/week-agenda/week-model';

export interface WeekStripDay {
  /** Household-local `YYYY-MM-DD`. */
  localDate: string;
  /** The statuses on this day (for the at-a-glance dots; up to 4 shown). */
  statuses: readonly OccurrenceResponseStatus[];
}

/**
 * Interactive 7-day strip — the calendar's date picker (mockup 02 screen 4). Each day is a button
 * showing its weekday + date + up to four status dots, so the whole week reads at a glance. Tapping a
 * day SELECTS it (the agenda below filters to that day); tapping the selected day again clears back to
 * the whole week. Today carries the primary highlight; the selected day carries a focus ring. Shared by
 * the kid and household views so both personas behave identically.
 */
export function WeekStrip({
  days,
  today,
  selected,
  onSelect,
}: {
  days: readonly WeekStripDay[];
  today: string;
  selected: string | null;
  onSelect: (localDate: string | null) => void;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const { locale } = useHouseholdContext();

  return (
    <ul className="flex justify-between gap-1" aria-label={t('calendar.weekStrip')}>
      {days.map((day) => {
        const date = parseLocalDate(day.localDate);
        const isToday = day.localDate === today;
        const isSelected = day.localDate === selected;
        // `date` is already anchored to the correct civil day at UTC noon (`parseLocalDate`) —
        // formatting must read it back with 'UTC', not the household's real timeZone, or a
        // UTC+12-or-later household rolls into the next calendar day (mirrors CalendarPage's header).
        const label = formatDay(locale, date, 'UTC');
        // The status the dots show by COLOUR alone is also folded into the accessible name, so a
        // screen-reader user gets each day's done/waiting/planned counts (WCAG 1.4.1).
        const c = countStatuses(day.statuses);
        const statusLabel =
          c.done + c.waiting + c.left > 0
            ? t('calendar.dayStatus', {
                done: c.done,
                waiting: c.waiting,
                left: c.left,
              })
            : null;
        return (
          <li key={day.localDate} className="flex-1">
            <button
              type="button"
              aria-pressed={isSelected}
              aria-label={statusLabel ? `${label}. ${statusLabel}` : label}
              {...(isToday ? { 'aria-current': 'date' as const } : {})}
              onClick={() => onSelect(isSelected ? null : day.localDate)}
              className={cn(
                'flex w-full flex-col items-center gap-1 rounded-md py-1.5 outline-none transition-colors',
                'focus-visible:ring-2 focus-visible:ring-ring',
                isToday ? 'bg-primary text-primary-fg' : 'text-text hover:bg-surface-muted',
                isSelected && !isToday && 'ring-2 ring-primary',
                isSelected && isToday && 'ring-2 ring-primary-fg',
              )}
            >
              <span className="text-[10px] font-semibold uppercase opacity-80">
                {formatWeekdayShort(locale, date, 'UTC')}
              </span>
              <span className="text-sm font-bold">{formatDayOfMonth(locale, date, 'UTC')}</span>
              <span className="flex min-h-2 items-center gap-0.5" aria-hidden="true">
                {day.statuses.slice(0, 4).map((status, i) => (
                  <span
                    key={i}
                    className={cn(
                      'size-1.5 rounded-pill',
                      isToday ? 'bg-primary-fg' : DOT_TONE[status],
                    )}
                  />
                ))}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
